/**
 * Responsibility: the drop-intelligence page - what the world is saying about
 * the products on the watchlist.
 *
 * Signals come first and the raw feed second, deliberately. The feed is the
 * interesting-looking part and the signals are the useful part: the operator
 * should see "two outlets agree about a product you track" before they see
 * fifty articles about Pokemon in general.
 *
 * Nothing on this page is stock. The watchlist answers whether something is
 * buyable; this answers whether something is coming, and the wording keeps
 * that separation explicit so a rumour is never mistaken for a restock.
 */
import { Alert, Box, Button, Chip, Link, Stack, Typography } from '@mui/material';
import { MetricCard, PageContainer, SectionCard } from '@kit/components';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DropSignalCard } from '../components/news/DropSignalCard';
import { SourceHealthPanel } from '../components/news/SourceHealthPanel';
import {
  useDropSignals,
  useNewsItems,
  useNewsSources,
  useSweepNews,
} from '../api/hooks/useNews';

function when(value: string | null, fallback: string): string {
  const iso = value ?? fallback;
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function NewsPage() {
  const { data: items, error: itemsError } = useNewsItems();
  const { data: signals } = useDropSignals();
  const { data: sources } = useNewsSources();
  const sweep = useSweepNews();

  const feed = items ?? [];
  const allSignals = signals ?? [];
  const corroborated = allSignals.filter((s) => s.strength !== 'RUMOUR');
  const rumours = allSignals.filter((s) => s.strength === 'RUMOUR');

  return (
    <PageContainer
    >
      {/* See WatchlistPage: a flex item defaults to min-width:auto, so anything
          with a large intrinsic width would widen this column and slide the
          whole page sideways on a phone. */}
      <Stack spacing={3} sx={{ minWidth: 0 }}>

      {itemsError ? <Alert severity="error">{(itemsError as Error).message}</Alert> : null}

      {sweep.isSuccess && !sweep.data.healthy ? (
        <Alert severity="warning">
          {sweep.data.sources_failed} of {sweep.data.sources_failed + sweep.data.sources_read}{' '}
          sources failed on the last sweep. A quiet result right now means nothing.
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
        }}
      >
        <MetricCard
          label="Corroborated"
          value={String(corroborated.length)}
          helper="agreed by several sources"
        />
        <MetricCard label="Rumours" value={String(rumours.length)} helper="single source only" />
        <MetricCard label="Articles" value={String(feed.length)} />
        <MetricCard
          label="Sources"
          value={String(sources?.enabled_sources ?? 0)}
          helper={
            sources && !sources.can_corroborate
              ? 'cannot reach the threshold'
              : `max score ${sources?.max_possible_score ?? 0}`
          }
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => sweep.mutate()}
          disabled={sweep.isPending}
        >
          {sweep.isPending ? 'Reading feeds' : 'Read feeds now'}
        </Button>
      </Box>

      <SectionCard
        title="Drop signals"
        subtitle="Products on your watchlist that the news is talking about. Only corroborated signals send a notification."
      >
        {allSignals.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing yet. Signals appear when an article names both a set you track and, where the
            article is specific about it, the packaging you track.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {allSignals.map((signal) => (
              <DropSignalCard key={signal.product_key} signal={signal} />
            ))}
          </Stack>
        )}
      </SectionCard>

      <SectionCard title="Feed" subtitle="Everything read, matched or not.">
        {feed.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No articles stored yet. The first sweep runs a minute after the hub starts.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {feed.map((item) => (
              <Box key={item.url}>
                <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                  <Link
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    underline="hover"
                    // 44px in both directions on touch. Height alone was
                    // not enough: a headline can be one short word - a
                    // Reddit post titled "Help" measured 30px wide - and
                    // the whole link is the tap target.
                    sx={{
                      fontWeight: 600,
                      minHeight: { xs: 44, md: 'auto' },
                      minWidth: { xs: 44, md: 'auto' },
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    {item.title}
                  </Link>
                  <Chip size="small" variant="outlined" label={item.source_name} />
                  <Typography variant="caption" color="text.secondary">
                    {when(item.published_at, item.fetched_at)}
                  </Typography>
                </Stack>
                {item.summary ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {item.summary}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Stack>
        )}
      </SectionCard>

      {sources ? <SourceHealthPanel health={sources} /> : null}
      </Stack>
    </PageContainer>
  );
}
